/**
 * Unified equipment catalog — extends the original detection-sensor catalog
 * (Optex initial seed) to cover all picker-driven equipment categories:
 * PIR motion, beam barriers, fence-tension, magnetic contacts, smoke/fire
 * detectors, glass-break, sirens (outdoor/indoor), panic buttons.
 *
 * The dialog (UnifiedEquipmentCatalogDialog) accepts a `category` prop and
 * filters this single source. Category-specific spec fields are typed via
 * a discriminated union so the picker UI can render the right chip set
 * per category without giant if-ladders.
 *
 * Data sources are cited per category — every SKU below is taken from a
 * primary manufacturer datasheet (Optex PDFs or SATEL 2026 catalog
 * MD-KATALOG-PL IND_0226). If a spec wasn't on the page, the field is
 * omitted rather than guessed.
 */
import type { PIRPattern } from './equipmentTypes';
import {
  OPTEX_DETECTION_SENSORS,
  type DetectionSensorCatalogEntry,
  type DetectionSensorPattern,
} from './detectionSensorCatalog';

// Re-export for callers that imported from the old module.
export { OPTEX_DETECTION_SENSORS, type DetectionSensorPattern };
export type { DetectionSensorCatalogEntry };

// The entry shape + its spec/category types moved to the leaf module
// `equipmentCatalogEntry.ts` to break the import cycle with the ~80 vendor
// catalog files (this module value-imports them; they type-import the entry
// shape back). Import the few used locally here, and re-export the whole set
// so existing `import { ... } from './equipmentCatalog'` call sites keep working.
import type {
  EquipmentCatalogEntry,
  EquipmentCategory,
  BaseEntry,
} from './equipmentCatalogEntry';
export type {
  EquipmentCatalogEntry,
  EquipmentCategory,
  BaseEntry,
  MagneticContactSpec,
  SmokeDetectorSpec,
  GlassBreakSpec,
  SirenSpec,
  PanicButtonSpec,
  AlarmPanelSpec,
  KeypadSpec,
  AccessReaderSpec,
  DoorControllerSpec,
  NVRSpec,
  PoESwitchSpec,
  AIBoxSpec,
  RouterSpec,
  RadioAntennaSpec,
  CellularAntennaSpec,
  WirelessAccessPointSpec,
  UPSSpec,
  IntercomSpec,
  IlluminatorSpec,
  SolarChargeControllerSpec,
  BatterySpec,
  WirelessExpanderSpec,
  BarrierGateSpec,
} from './equipmentCatalogEntry';

/**
 * Map from category → `EquipmentType` placement target. The picker calls
 * setActiveTool with this when the user commits a catalog entry.
 */
export const CATEGORY_TO_EQUIPMENT_TYPE: Record<
  EquipmentCategory,
  | 'pir-motion-sensor'
  | 'magnetic-contact'
  | 'smoke-detector'
  | 'glass-break'
  | 'outdoor-siren'
  | 'indoor-sounder'
  | 'panic-button'
  | 'alarm-control-panel'
  | 'keypad'
  | 'access-control-reader'
  | 'door-access-controller'
  | 'intercom'
  | 'recorder'
  | 'network-closet'
  | 'poe-switch'
  | 'router'
  | 'radio-antenna'
  | 'cellular-antenna'
  | 'wireless-access-point'
  | 'ups'
  | 'barrier-gate'
  | 'speaker-horn'
  | 'media-converter'
  | 'poe-injector'
  | 'power-supply'
  | 'pole-enclosure'
  | 'pc-workstation'
> = {
  'outdoor-wall-pir': 'pir-motion-sensor',
  'indoor-ceiling-pir': 'pir-motion-sensor',
  'curtain-pir': 'pir-motion-sensor',
  'boundary-pir': 'pir-motion-sensor',
  'beam-barrier': 'pir-motion-sensor', // beam pairs use PIR equipment with pirPattern='beam'
  'fence-detector': 'pir-motion-sensor',
  'magnetic-contact': 'magnetic-contact',
  'smoke-detector': 'smoke-detector',
  'glass-break': 'glass-break',
  'outdoor-siren': 'outdoor-siren',
  'indoor-sounder': 'indoor-sounder',
  'panic-button': 'panic-button',
  'alarm-panel': 'alarm-control-panel',
  'keypad': 'keypad',
  'access-reader': 'access-control-reader',
  'door-controller': 'door-access-controller',
  'intercom': 'intercom',
  // Wireless RF hub mounts at / wires into the panel — reuse the panel glyph.
  'wireless-expander': 'alarm-control-panel',
  'nvr-recorder': 'recorder',
  // A placed PoE switch is its OWN EquipmentType (distinct from the
  // 'network-closet' rack cabinet) so it is IP/PoE-capable, rack-mountable,
  // named SW, and rendered as a switch. Legacy projects that stored these as
  // 'network-closet' are converted by the load-time migration.
  'poe-switch': 'poe-switch',
  // AI Boxes are physically rack appliances — share the recorder placement
  // glyph in the designer. Picker / BOM still surface the distinct category.
  'ai-box': 'recorder',
  // Network infrastructure → own EquipmentTypes (router/radio-antenna/
  // cellular-antenna) so the designer renders each with its dedicated
  // glyph + behaviour (radio antenna draws TX↔RX line, etc.).
  'router': 'router',
  'radio-antenna': 'radio-antenna',
  'cellular-antenna': 'cellular-antenna',
  'wireless-access-point': 'wireless-access-point',
  'ups': 'ups',
  // Bare standby battery shares the UPS "power-backup" glyph + placement.
  'battery': 'ups',
  // Solar charge controller is a power/backup device — shares the UPS glyph
  // + placement (same family as battery→ups).
  'solar-charge-controller': 'ups',
  // IR illuminator is a directional pole/wall-mounted emitter — absent a
  // dedicated lighting glyph it reuses the speaker-horn placement (the only
  // other mounted directional-emitter glyph with a render path).
  'ir-illuminator': 'speaker-horn',
  // Pole-side distribution: each category is its own type (see EquipmentType).
  'media-converter': 'media-converter',
  'poe-injector': 'poe-injector',
  'power-supply': 'power-supply',
  'pole-enclosure': 'pole-enclosure',
  // Barrier gate → its own placement type (renders cabinet + boom arm).
  'barrier-gate': 'barrier-gate',
  // Fall radar reuses the PIR pattern/range machinery (like beam/fence).
  'fall-detection-radar': 'pir-motion-sensor',
  'workstation': 'pc-workstation',
  'ip-speaker': 'speaker-horn',
};

/**
 * Type guards for the discriminated union — the picker uses these to
 * narrow to the right spec block before rendering category-specific chips.
 */
export function isDetectionSensorEntry(
  entry: EquipmentCatalogEntry,
): entry is DetectionSensorCatalogEntry & BaseEntry {
  return (
    entry.category === 'outdoor-wall-pir' ||
    entry.category === 'indoor-ceiling-pir' ||
    entry.category === 'curtain-pir' ||
    entry.category === 'boundary-pir' ||
    entry.category === 'beam-barrier' ||
    entry.category === 'fence-detector' ||
    entry.category === 'fall-detection-radar'
  );
}

// ── Catalog merge: Optex + SATEL + alarm-system + per-brand catalogs ────

import { SATEL_EQUIPMENT_CATALOG } from './satelEquipmentCatalog';
import { ALARM_SYSTEM_CATALOG } from './alarmSystemCatalog';
import { BA_BATTERY_CATALOG } from './baBatteryCatalog';
import { HIKVISION_CATALOG } from './hikvisionCatalog';
import { DAHUA_CATALOG } from './dahuaCatalog';
import { AJAX_AND_HANWHA_CATALOG } from './ajaxAndHanwhaCatalog';
import { NETWORK_SWITCH_CATALOG } from './networkSwitchCatalog';
import { MID_TIER_NVR_CATALOG } from './midTierNVRCatalog';
import { BOSCH_CATALOG } from './boschCatalog';
import { ALARM_SPECIALISTS_CATALOG } from './alarmSpecialistsCatalog';
import { HIKVISION_NVR_DEEP_DIVE } from './hikvisionNVRDeepDive';
import { NETWORK_SWITCH_EXT_CATALOG } from './networkSwitchExtCatalog';
import { DAHUA_NVR_DEEP_DIVE } from './dahuaNVRDeepDive';
import { HIKVISION_NVR_PRO } from './hikvisionNvrProCatalog';
import { HIKVISION_NVR_ULTRA } from './hikvisionNvrUltraCatalog';
import { DAHUA_NVR_5000_6000 } from './dahua-nvr-5000-6000';
import { HIKVISION_DVR_TURBO } from './hikvision-dvr-turbo';
import { DAHUA_NVR_4000 } from './dahua-nvr-4000';
import { DAHUA_XVR } from './dahua-xvr';
import { HANWHA_DEEP } from './hanwha-deep';
import { DSC_PARADOX_ELDES } from './dsc-paradox-eldes';
import { BOSCH_EXTENDED } from './bosch-extended';
import { PERIMETER_DEEP } from './perimeter-deep';
import { INTERCOMS_DEEP } from './intercoms-deep';
import { ENTERPRISE_SWITCHES } from './enterprise-switches';
import { AXIS_DEEP } from './axis-deep';
import { IPRO_RECORDERS } from './ipro-recorders.generated';
import { HIKVISION_EQUIPMENT } from './hikvision-equipment.generated';
import { SUNELL_EQUIPMENT } from './sunell-equipment.generated';
import { MILESIGHT_EQUIPMENT } from './milesight-equipment.generated';
import { TVT_EQUIPMENT } from './tvt-equipment.generated';
import { TPLINK_CN_EQUIPMENT } from './tplink-cn-equipment.generated';
import { HIKVISION_CN_EQUIPMENT } from './hikvision-cn-equipment.generated';
import { CYBERPOWER_UPS } from './cyberpower-ups.generated';
import { DAHUA_ARCHIVED_EQUIPMENT } from './dahua-archived-equipment.generated';
import { PULSAR_POWER_SUPPLIES } from './pulsar-power-supplies.generated';
import { UNIVIEW_EQUIPMENT } from './uniview-equipment.generated';
import { RECORDERS_FROM_CAMERA_ROWS } from './recorders-from-camera-rows.generated';
import { HONEYWELL_DEEP } from './honeywell-deep';
import { UBIQUITI_CATALOG } from './ubiquitiCatalog';
import { TPLINK_OMADA_CATALOG } from './tplinkOmadaCatalog';
import { NETGEAR_AV_LINE_CATALOG } from './netgearAvLineCatalog';
import { MIKROTIK_SWITCH_CATALOG } from './mikrotikSwitchCatalog';
import { UNIVIEW_SWITCH_CATALOG } from './univiewSwitchCatalog';
import { REOLINK_NVR_CATALOG } from './reolinkNvrCatalog';
import { KENIK_NVR_CATALOG } from './kenikNvrCatalog';
import { SYNOLOGY_QNAP_NVR_CATALOG } from './synologyQnapNvrCatalog';
import { HIKVISION_INTERCOM_CATALOG } from './hikvisionIntercomCatalog';
import { DAHUA_INTERCOM_CATALOG } from './dahuaIntercomCatalog';
import { DOORBIRD_BASIP_INTERCOM_CATALOG } from './doorbirdBasipIntercomCatalog';
import { HID_SUPREMA_ACCESS_CATALOG } from './hidSupremaAccessCatalog';
import { PAXTON_ZKTECO_ACCESS_CATALOG } from './paxtonZktecoAccessCatalog';
import { HONEYWELL_PYRONIX_RISCO_TEXECOM_PIR } from './honeywellPyronixRiscoTexecomPirCatalog';
import { VIVOTEK_MOBOTIX_NVR_CATALOG } from './vivotekMobotixNvrCatalog';
import { MOBOTIX_INTERCOM_CATALOG } from './mobotixIntercomCatalog';
import { CYGNUS_FIRE_CATALOG } from './cygnusFireCatalog';
import { AJAX_FIRE_CATALOG } from './ajaxFireCatalog';
import { AJAX_SUPERIOR_CATALOG } from './ajaxSuperiorCatalog';
import { FALL_DETECTION_SENSORS } from './fallDetectionCatalog';
import {
  REGIONAL_ADDITIONS_CATALOG,
  REGIONAL_PIR_DETECTORS,
} from './regional-additions';
import { AI_BOX_CATALOG } from './ai-box-catalog';
import { MIKROTIK_ROUTERS } from './mikrotik-routers';
import { MIKROTIK_RADIO_ANTENNAS } from './mikrotik-radio-antennas';
import { MIKROTIK_CELLULAR_ANTENNAS } from './mikrotik-cellular-antennas';
import { TELTONIKA_CELLULAR_ANTENNAS } from './teltonika-cellular-antennas';
import { TPLINK_OMADA_ROUTERS } from './tplink-omada-routers';
import { UBIQUITI_RADIO_ANTENNAS } from './ubiquiti-radio-antennas';
import { CAMBIUM_RADIO_ANTENNAS } from './cambium-radio-antennas';
import { POYNTING_CELLULAR_ANTENNAS } from './poynting-cellular-antennas';
import { ENTERPRISE_CELLULAR_ANTENNAS } from './enterprise-cellular-antennas';
import { INDUSTRIAL_CELLULAR_ANTENNAS } from './industrial-cellular-antennas';
import { ATTE_POE_SWITCHES } from './atte-poe-switches';
import { TPLINK_RADWIN_RADIO_ANTENNAS } from './tplink-radwin-radio-antennas';
import { INFINET_PROXIM_RADIO_ANTENNAS } from './infinet-proxim-radio-antennas';
import { WIRELESS_ACCESS_POINTS } from './wireless-access-points';
import { ENTERPRISE_INDUSTRIAL_NETWORK } from './enterprise-industrial-network';
import { PULSAR_POE_SWITCHES } from './pulsar-poe-switches';
import { UPS_CATALOG } from './ups-catalog';
import { ILLUMINATOR_CATALOG } from './illuminatorCatalog';
import { SATEL_ALARM_PANEL_AUTO } from './auto-extracted/satel-alarm-panel';
import { BULK_EQUIPMENT_2026_05 } from './auto-extracted/2026-05-bulk-equipment';
import { SS_ALARM_PANELS } from './auto-extracted/ss-alarm-panels';
import { SS_NVR_RECORDERS } from './auto-extracted/ss-nvr-recorders';
import { SS_KEYPADS } from './auto-extracted/ss-keypads';
import { SS_OUTDOOR_SIRENS } from './auto-extracted/ss-outdoor-sirens';
import { SS_INDOOR_SOUNDERS } from './auto-extracted/ss-indoor-sounders';
import { SS_PANIC_BUTTONS } from './auto-extracted/ss-panic-buttons';
import { SPRINT4_EQUIPMENT_ADDITIONS } from './sprint4-equipment-additions';
import { BARRIER_GATE_CATALOG } from './barrier-gate-catalog';
// Catalogs migrated from camera-additions.json — SKUs that the equipment
// scraper had misclassified as cameras. Top of source list so deeper
// hand-curated catalogs (HIKVISION_NVR_DEEP_DIVE, DAHUA_NVR_5000_6000 etc.)
// override these on id collision under the last-wins dedupe rule.
import { MIGRATED_POE_SWITCHES } from './migrated-poe-switches';
import { MIGRATED_NVRS } from './migrated-nvrs';
import { MIGRATED_DVRS } from './migrated-dvrs';
import { MIGRATED_ACCESS_CONTROL } from './migrated-access-control';
import { MIGRATED_INTERCOMS } from './migrated-intercoms';
import { MIGRATED_ALARM_PANELS } from './migrated-alarm-panels';
import { POLE_DISTRIBUTION_GENERICS } from './pole-distribution-generics';
import { POLE_DISTRIBUTION_CATALOG } from './pole-distribution-catalog';
import { RETIRED_EQUIPMENT, RETIRED_EQUIPMENT_SUCCESSOR } from './retired-equipment.generated';

/**
 * The SourceSecurity NVR scrape (`SS_NVR_RECORDERS`, all `verified:false`)
 * ships ~777 entries with `channels: 0`. Those aren't recorders — they're
 * accessories the scraper filed under "nvr-recorder": HDD caddies, RAM
 * alarm-input cards, POS/ATM interface modules, backup dongles. Merging them
 * verbatim both pollutes the NVR picker AND silently defeats rack/port
 * validation: `getPortLimit()` returns `null` for an nvr-recorder with
 * `channels <= 0` and no `poeInPorts`, i.e. the "recorder" is treated as
 * having unlimited cable terminations. Drop the channel-less entries at the
 * merge; keep every entry that exposes a real channel count.
 */
const SS_NVR_RECORDERS_FILTERED: EquipmentCatalogEntry[] = SS_NVR_RECORDERS.filter(
  // Drop only the entries that actually defeat rack/port validation: an
  // nvr-recorder with no channel count AND no PoE ports (getPortLimit() returns
  // null → treated as unlimited). Keep channel-less entries that expose real
  // poeInPorts — those still carry a valid termination budget.
  (e) =>
    !(
      e.category === 'nvr-recorder' &&
      (e.specs.channels ?? 0) <= 0 &&
      (e.specs.poeInPorts ?? 0) <= 0
    ),
);

if ((import.meta as { env?: { DEV?: boolean } }).env?.DEV) {
  const dropped = SS_NVR_RECORDERS.length - SS_NVR_RECORDERS_FILTERED.length;
  if (dropped > 0) {
    console.warn(
      `[equipmentCatalog] dropped ${dropped} SS_NVR_RECORDERS entries with ` +
        `channels<=0 (accessories miscategorized as nvr-recorder — would ` +
        `defeat rack/port validation).`,
    );
  }
}

/**
 * Single source of truth for all equipment catalog entries. Several brand
 * catalogs overlap (e.g. the original `hikvisionCatalog` covers headline
 * SKUs that deep-scan files like `hikvisionNvrProCatalog` also include).
 * Dedupe by id at merge time — last-wins so the deeper / newer scan
 * overrides the seed entry.
 */
const _ALL_CATALOG_SOURCES: EquipmentCatalogEntry[] = [
  // ── Migration catalogs FIRST so hand-curated deep-dives override on ──
  // ── id collision (last-wins dedupe inside getCatalog()).            ──
  ...MIGRATED_POE_SWITCHES,
  ...MIGRATED_NVRS,
  ...MIGRATED_DVRS,
  ...MIGRATED_ACCESS_CONTROL,
  ...MIGRATED_INTERCOMS,
  ...MIGRATED_ALARM_PANELS,
  ...(OPTEX_DETECTION_SENSORS as EquipmentCatalogEntry[]),
  ...SATEL_EQUIPMENT_CATALOG,
  ...ALARM_SYSTEM_CATALOG,
  ...BA_BATTERY_CATALOG,
  ...HIKVISION_CATALOG,
  ...DAHUA_CATALOG,
  ...AJAX_AND_HANWHA_CATALOG,
  ...NETWORK_SWITCH_CATALOG,
  ...MID_TIER_NVR_CATALOG,
  ...BOSCH_CATALOG,
  ...ALARM_SPECIALISTS_CATALOG,
  ...HIKVISION_NVR_DEEP_DIVE,
  ...NETWORK_SWITCH_EXT_CATALOG,
  ...DAHUA_NVR_DEEP_DIVE,
  ...HIKVISION_NVR_PRO,
  ...HIKVISION_NVR_ULTRA,
  ...HIKVISION_DVR_TURBO,
  ...DAHUA_NVR_5000_6000,
  ...DAHUA_NVR_4000,
  ...DAHUA_XVR,
  ...HANWHA_DEEP,
  ...DSC_PARADOX_ELDES,
  ...BOSCH_EXTENDED,
  ...(PERIMETER_DEEP as EquipmentCatalogEntry[]),
  ...INTERCOMS_DEEP,
  ...ENTERPRISE_SWITCHES,
  ...AXIS_DEEP,
  ...IPRO_RECORDERS,
  ...HIKVISION_EQUIPMENT,
  ...SUNELL_EQUIPMENT,
  ...MILESIGHT_EQUIPMENT,
  ...TVT_EQUIPMENT,
  ...TPLINK_CN_EQUIPMENT,
  ...HIKVISION_CN_EQUIPMENT,
  ...CYBERPOWER_UPS,
  ...DAHUA_ARCHIVED_EQUIPMENT,
  ...PULSAR_POWER_SUPPLIES,
  ...UNIVIEW_EQUIPMENT,
  ...RECORDERS_FROM_CAMERA_ROWS,
  ...HONEYWELL_DEEP,
  ...UBIQUITI_CATALOG,
  ...TPLINK_OMADA_CATALOG,
  ...NETGEAR_AV_LINE_CATALOG,
  ...MIKROTIK_SWITCH_CATALOG,
  ...UNIVIEW_SWITCH_CATALOG,
  ...REOLINK_NVR_CATALOG,
  ...KENIK_NVR_CATALOG,
  ...SYNOLOGY_QNAP_NVR_CATALOG,
  ...HIKVISION_INTERCOM_CATALOG,
  ...DAHUA_INTERCOM_CATALOG,
  ...DOORBIRD_BASIP_INTERCOM_CATALOG,
  ...HID_SUPREMA_ACCESS_CATALOG,
  ...PAXTON_ZKTECO_ACCESS_CATALOG,
  ...(HONEYWELL_PYRONIX_RISCO_TEXECOM_PIR as EquipmentCatalogEntry[]),
  ...VIVOTEK_MOBOTIX_NVR_CATALOG,
  ...MOBOTIX_INTERCOM_CATALOG,
  ...CYGNUS_FIRE_CATALOG,
  ...AJAX_FIRE_CATALOG,
  ...AJAX_SUPERIOR_CATALOG,
  ...(FALL_DETECTION_SENSORS as EquipmentCatalogEntry[]),
  ...(REGIONAL_PIR_DETECTORS as EquipmentCatalogEntry[]),
  ...REGIONAL_ADDITIONS_CATALOG,
  ...AI_BOX_CATALOG,
  ...MIKROTIK_ROUTERS,
  ...MIKROTIK_RADIO_ANTENNAS,
  ...MIKROTIK_CELLULAR_ANTENNAS,
  ...TELTONIKA_CELLULAR_ANTENNAS,
  ...TPLINK_OMADA_ROUTERS,
  ...UBIQUITI_RADIO_ANTENNAS,
  ...CAMBIUM_RADIO_ANTENNAS,
  ...POYNTING_CELLULAR_ANTENNAS,
  ...ENTERPRISE_CELLULAR_ANTENNAS,
  ...INDUSTRIAL_CELLULAR_ANTENNAS,
  ...ATTE_POE_SWITCHES,
  ...TPLINK_RADWIN_RADIO_ANTENNAS,
  ...INFINET_PROXIM_RADIO_ANTENNAS,
  ...WIRELESS_ACCESS_POINTS,
  ...ENTERPRISE_INDUSTRIAL_NETWORK,
  ...PULSAR_POE_SWITCHES,
  ...UPS_CATALOG,
  ...ILLUMINATOR_CATALOG,
  ...SATEL_ALARM_PANEL_AUTO,
  ...BULK_EQUIPMENT_2026_05,
  ...SS_ALARM_PANELS,
  ...SS_NVR_RECORDERS_FILTERED,
  ...SS_KEYPADS,
  ...SS_OUTDOOR_SIRENS,
  ...SS_INDOOR_SOUNDERS,
  ...SS_PANIC_BUTTONS,
  ...SPRINT4_EQUIPMENT_ADDITIONS,
  ...BARRIER_GATE_CATALOG,
  ...POLE_DISTRIBUTION_GENERICS,
  ...POLE_DISTRIBUTION_CATALOG,
];

const KEEP_EARLIER_FIGURES = new Set(['ipro-wj-nx510k', 'hikvision-ds-7108ni-q1-m']);

export const EQUIPMENT_CATALOG: EquipmentCatalogEntry[] = (() => {
  const byId = new Map<string, EquipmentCatalogEntry>();
  for (const entry of _ALL_CATALOG_SOURCES) {
    // the maker-page read of these two states fewer figures than their datasheet entry: it adds to them, not drops them
    const prev = byId.get(entry.id) as { specs?: object } | undefined;
    const next = entry as { specs?: object };
    const keep = KEEP_EARLIER_FIGURES.has(entry.id) && prev?.specs && next.specs;
    byId.set(entry.id, keep ? ({ ...entry, specs: { ...prev.specs, ...next.specs } } as EquipmentCatalogEntry) : entry);
  }
  // ids saved projects may still name: hidden, resolved to the same model's entry or kept as they were
  for (const [id, to] of Object.entries(RETIRED_EQUIPMENT_SUCCESSOR)) {
    const entry = byId.get(to);
    if (entry && !byId.has(id)) byId.set(id, { ...entry, id, hidden: true });
  }
  for (const entry of RETIRED_EQUIPMENT) if (!byId.has(entry.id)) byId.set(entry.id, entry);
  return [...byId.values()];
})();

/**
 * Fast lookup by id (e.g. for resolving a recent/most-used pick from the
 * stored id back to the full entry, or for the BOM to enrich placed
 * equipment from its `catalogId`).
 */
export function getCatalogEntryById(id: string): EquipmentCatalogEntry | undefined {
  return EQUIPMENT_CATALOG.find((e) => e.id === id) ?? runtimeEntries.get(id);
}

// ── Entries known only at run time ─────────────────────────────────────────
// Products a manufacturer's organisation added itself (org-catalog, ids
// 'org-<uuid>'), the user's team library of own equipment ('custom-<uuid>')
// and the copies of it a project carries. Registered once loaded so every
// getCatalogEntryById consumer (BOM, validator, edit panel) resolves a placed
// one like a compiled entry. Each source replaces only its own entries.
export type RuntimeCatalogSource = 'org' | 'custom' | 'project';
const runtimeSources = new Map<RuntimeCatalogSource, Map<string, EquipmentCatalogEntry>>();
const runtimeEntries = new Map<string, EquipmentCatalogEntry>();

function rebuildRuntimeEntries(): void {
  runtimeEntries.clear();
  // Later sources win: a project's copy of a custom item gives way to the
  // live library entry when the user has it.
  for (const source of ['project', 'org', 'custom'] as const) {
    for (const [id, e] of runtimeSources.get(source) ?? []) runtimeEntries.set(id, e);
  }
}

export function setRuntimeCatalogEntries(entries: EquipmentCatalogEntry[], source: RuntimeCatalogSource = 'org'): void {
  runtimeSources.set(source, new Map(entries.map((e) => [e.id, e])));
  rebuildRuntimeEntries();
}

export function getRuntimeCatalogEntries(): EquipmentCatalogEntry[] {
  return [...runtimeEntries.values()];
}

/**
 * Filter to a single category. Excludes hidden entries. Useful for the
 * picker's main grid + for inventorying category coverage.
 */
export function getCatalogByCategory(category: EquipmentCategory): EquipmentCatalogEntry[] {
  return [...EQUIPMENT_CATALOG, ...runtimeEntries.values()].filter((e) => e.category === category && !e.hidden);
}

/**
 * Distinct manufacturers in a category — feeds the manufacturer chip
 * filter in the picker ("All / Optex / SATEL"). Sorted alphabetically.
 */
export function getManufacturersInCategory(category: EquipmentCategory): string[] {
  const set = new Set<string>();
  for (const e of [...EQUIPMENT_CATALOG, ...runtimeEntries.values()]) {
    if (e.category === category && !e.hidden) set.add(e.manufacturer);
  }
  return [...set].sort();
}

/** PIR pattern fallback unused — kept so callers can import from one module. */
export type { PIRPattern };

// ── Pending catalog-pick handshake (NON-PIR equipment) ──────────────────
//
// PIR / beam / fence types use the existing
// `setPendingDetectionSensor` mechanism in detectionSensorCatalog.ts
// (preserved for backward compat with the existing addEquipment flow).
//
// For the new equipment types (magnetic-contact, smoke-detector,
// glass-break, outdoor-siren, indoor-sounder, panic-button) we use this
// parallel handshake: picker sets, addEquipment consumes, picker tool
// switches active and waits for the next map click. Module-level state
// is the same pragmatic trade-off as the PIR pending mechanism — single
// designer instance per page, self-clears after consumption, leakage
// impossible because the next picker open overwrites.
//
// Why separate from `setPendingDetectionSensor`: that one's typed as
// DetectionSensorCatalogEntry which has PIR-specific fields (rangeM,
// pattern). Non-PIR entries can't be coerced into that shape without
// littering them with phantom 0/undefined PIR fields.

let pendingNonPirCatalog: EquipmentCatalogEntry | null = null;

export function setPendingEquipmentCatalog(entry: EquipmentCatalogEntry | null): void {
  pendingNonPirCatalog = entry;
}

/**
 * Returns and clears the pending catalog entry. Called from addEquipment
 * for non-PIR equipment types so the new Equipment can be enriched with
 * `catalogId` / `catalogManufacturer` / `catalogModel` (which the BOM
 * already knows how to use for grouped line items).
 */
export function consumePendingEquipmentCatalog(): EquipmentCatalogEntry | null {
  const e = pendingNonPirCatalog;
  pendingNonPirCatalog = null;
  return e;
}

export function peekPendingEquipmentCatalog(): EquipmentCatalogEntry | null {
  return pendingNonPirCatalog;
}

// ────────────────────────────────────────────────────────────────────────────
// Radio-antenna TX/RX pair handshake.
//
// Mirrors `pendingBeamTxId` from detectionSensorCatalog.ts — radio antennas
// are PtP wireless bridges where the user clicks twice (TX then RX). The
// first click places the transmitter and stashes its equipment id here;
// the second click reads that id, places the receiver, links them via
// `radioPairedId`/`radioRole`, and clears the slot.
//
// Same module-level ref pattern + rationale as the beam-pair flow: single
// designer instance per page, self-clears when the pair completes. If the
// user abandons the placement mid-pair, the orphan TX stays on the map
// and they can delete it manually (same as orphan beam TX behaviour).
// ────────────────────────────────────────────────────────────────────────────
let pendingRadioAntennaTxId: string | null = null;

export function setPendingRadioAntennaTxId(id: string | null): void {
  pendingRadioAntennaTxId = id;
}
export function consumePendingRadioAntennaTxId(): string | null {
  const id = pendingRadioAntennaTxId;
  pendingRadioAntennaTxId = null;
  return id;
}
export function peekPendingRadioAntennaTxId(): string | null {
  return pendingRadioAntennaTxId;
}

// ────────────────────────────────────────────────────────────────────────────
// Radio-antenna placement mode.
//
// User can choose between two placement flows after picking a radio model:
//   • 'pair'   — current default: two-click TX+RX bridge with auto-linking
//   • 'single' — drop a single antenna (for 1-RX/multi-TX hubs, retrofits,
//                or replacing one end of an existing link)
//
// Set once when the placement-mode dialog confirms; read by addEquipment to
// decide whether to take the TX/RX handshake branch or the single-click branch.
// Self-clears when consumed, so leaking into the next placement is impossible.
// ────────────────────────────────────────────────────────────────────────────
let pendingRadioPlacementMode: 'pair' | 'single' | null = null;

export function setPendingRadioPlacementMode(mode: 'pair' | 'single' | null): void {
  pendingRadioPlacementMode = mode;
}
export function consumePendingRadioPlacementMode(): 'pair' | 'single' | null {
  const m = pendingRadioPlacementMode;
  pendingRadioPlacementMode = null;
  return m;
}
export function peekPendingRadioPlacementMode(): 'pair' | 'single' | null {
  return pendingRadioPlacementMode;
}

// ────────────────────────────────────────────────────────────────────────────
// Radio BRIDGE placement (the dedicated "Radio Bridge" tool).
//
// Unlike the single-antenna picker above, a bridge carries TWO catalog models
// — TX and RX can differ (e.g. a directional dish at one end, a 360° omni at
// the other). The dialog confirms a config; the map-click flow then places the
// TX, stashes it back here (txId/txLat/txLng/txRangeM), and on the second
// step either places a new RX or links the TX to an EXISTING RX antenna.
//
// rxMode:
//   • 'new'      — second click drops a fresh RX of rxCatalogId, auto-linked.
//   • 'existing' — second click must hit an existing RX antenna; the new TX
//                  links to it (a 360° omni can be shared by many TX = PtMP).
// ────────────────────────────────────────────────────────────────────────────
export interface PendingRadioBridge {
  txCatalogId: string;
  /** Only used in 'new' mode — in 'existing' mode the RX keeps its own model. */
  rxCatalogId?: string;
  rxMode: 'new' | 'existing';
  // Populated after the TX is placed (step 1 → step 2 handoff):
  txId?: string;
  txLat?: number;
  txLng?: number;
  txRangeM?: number;
}
let pendingRadioBridge: PendingRadioBridge | null = null;

export function setPendingRadioBridge(b: PendingRadioBridge | null): void {
  pendingRadioBridge = b;
}
export function consumePendingRadioBridge(): PendingRadioBridge | null {
  const b = pendingRadioBridge;
  pendingRadioBridge = null;
  return b;
}
export function peekPendingRadioBridge(): PendingRadioBridge | null {
  return pendingRadioBridge;
}
